import React, { useEffect, useState } from 'react';
import Modal from '../../components/components/modal/modal.jsx';
import ApiService from '../../components/api_services/apiService.jsx';
import { ReactComponent as StarIcon } from '../../components/icons/star.svg';
import { ReactComponent as AngleDown } from '../../components/icons/angle-down.svg';
import {
    UI_PLAN_TO_API,
    API_PLAN_TO_UI,
    UI_PLAN_DURATION_DAYS,
    formatDateForInput,
    formatPromotionDate,
    getPromotionStartDateBounds,
    validatePromotionStartDate,
    getPromotionTruncationNotice,
    filterPromotableEvents,
    isDateWithinPromotionStartBounds,
} from './promoUtils.js';
import PromotionStartDatePicker from './PromotionStartDatePicker.jsx';

const defaultForm = {
    categoryId: '',
    eventId: '',
    promotionType: '',
    startDate: '',
};

function PromoEventModal({ isOpen, onClose, onSubmit, initialData = null }) {
    const [formData, setFormData] = useState(defaultForm);
    const [categories, setCategories] = useState([]);
    const [events, setEvents] = useState([]);

    useEffect(() => {
        if (!isOpen) return;

        const loadData = async () => {
            let loadedCategories = [];
            let loadedEvents = [];

            try {
                const categoriesResponse = await ApiService.getEventsCategories();
                loadedCategories = categoriesResponse?.data || categoriesResponse || [];
            } catch (error) {
                loadedCategories = [];
            }

            try {
                const eventsResponse = await ApiService.getEvents(null, null, 1, null, null, 500);
                loadedEvents = eventsResponse?.data || [];
            } catch (error) {
                loadedEvents = [];
            }

            if (initialData?.eventId && !loadedEvents.some((event) => String(event.id) === String(initialData.eventId))) {
                try {
                    const eventResponse = await ApiService.getEvent(initialData.eventId);
                    const eventData = eventResponse?.data ?? eventResponse;
                    if (eventData?.id) {
                        loadedEvents = [...loadedEvents, eventData];
                    }
                } catch (error) {
                    // optional fallback when paginated list misses the event
                }
            }

            setCategories(loadedCategories);
            setEvents(filterPromotableEvents(loadedEvents));
        };

        loadData();
    }, [isOpen, initialData?.eventId]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!initialData) {
            setFormData(defaultForm);
            return;
        }

        const matchedEvent = events.find((event) => String(event.id) === String(initialData.eventId));
        const promotionType = initialData.promotionType
            || API_PLAN_TO_UI[initialData.planType]
            || '';

        setFormData({
            categoryId: matchedEvent
                ? String(matchedEvent.categoryId)
                : (initialData.categoryId ? String(initialData.categoryId) : ''),
            eventId: initialData.eventId ? String(initialData.eventId) : '',
            promotionType,
            startDate: initialData.startDate || formatDateForInput(initialData.startsAt) || '',
        });
    }, [initialData, isOpen, events]);

    const filteredEvents = formData.categoryId
        ? events.filter((event) => String(event.categoryId) === String(formData.categoryId))
        : events;

    const selectedEvent = events.find((event) => String(event.id) === String(formData.eventId));
    const { min: minStartDate, max: maxStartDate } = getPromotionStartDateBounds(selectedEvent);
    const planDurationDays = UI_PLAN_DURATION_DAYS[formData.promotionType];
    const truncationNotice = getPromotionTruncationNotice(
        formData.startDate,
        planDurationDays,
        selectedEvent?.endDate
    );

    useEffect(() => {
        if (!formData.startDate || !selectedEvent) {
            return;
        }
        const { min, max } = getPromotionStartDateBounds(selectedEvent);
        if (!isDateWithinPromotionStartBounds(formData.startDate, min, max)) {
            setFormData((prev) => ({ ...prev, startDate: '' }));
        }
    }, [formData.eventId, selectedEvent, formData.startDate]);

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleStartDateChange = (startDate) => {
        setFormData((prev) => ({ ...prev, startDate }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const eventId = Number(formData.eventId);
        const planType = initialData?.planType || UI_PLAN_TO_API[formData.promotionType];

        if (!formData.eventId || !Number.isFinite(eventId) || eventId <= 0) {
            alert('Wybierz wydarzenie.');
            return;
        }
        if (!planType) {
            alert('Wybierz rodzaj promowania.');
            return;
        }

        const dateValidationError = validatePromotionStartDate(
            formData.startDate,
            selectedEvent,
            planDurationDays
        );
        if (dateValidationError) {
            alert(dateValidationError);
            return;
        }

        const payload = {
            eventId,
            planType,
            startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        };

        if (onSubmit) {
            onSubmit(payload);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="promo-modal-content">
            <div className="promo-modal-header">
                <StarIcon className="promo-modal-icon" />
                <h2>Promuj wydarzenie</h2>
            </div>

            <form className="promo-modal-form" onSubmit={handleSubmit}>
                <div className="promo-modal-row">
                    <label>Kategoria wydarzenia</label>
                    <div className="promo-modal-select">
                        <select value={formData.categoryId} onChange={handleChange('categoryId')}>
                            <option value="">Wszystkie</option>
                            {categories.map((category) => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                        </select>
                        <AngleDown className="promo-modal-select-icon" />
                    </div>
                </div>

                <div className="promo-modal-row">
                    <label>Wydarzenie</label>
                    <div className="promo-modal-select">
                        <select value={formData.eventId} onChange={handleChange('eventId')} required>
                            <option value="">Wybierz</option>
                            {filteredEvents.map((event) => (
                                <option key={event.id} value={event.id}>{event.name}</option>
                            ))}
                        </select>
                        <AngleDown className="promo-modal-select-icon" />
                    </div>
                </div>

                <div className="promo-modal-row">
                    <label>Rodzaj promowania</label>
                    <div className="promo-modal-select">
                        <select value={formData.promotionType} onChange={handleChange('promotionType')} required>
                            <option value="">Wybierz</option>
                            <option value="Basic">Basic</option>
                            <option value="Standard">Standard</option>
                            <option value="Visibility+">Visibility+</option>
                            <option value="Premium">Premium</option>
                        </select>
                        <AngleDown className="promo-modal-select-icon" />
                    </div>
                </div>

                <div className="promo-modal-row">
                    <label>Data rozpoczęcia promowania</label>
                    <div className="promo-modal-date">
                        <PromotionStartDatePicker
                            value={formData.startDate}
                            onChange={handleStartDateChange}
                            minDate={minStartDate}
                            maxDate={maxStartDate}
                            durationDays={planDurationDays}
                            eventEndDate={selectedEvent?.endDate}
                        />
                    </div>
                    {selectedEvent && (
                        <p className="promo-modal-date-hint">
                            Pozostaw puste, aby rozpocząć promowanie od razu.
                            {' '}Start od dziś. Promowanie kończy się najpóźniej wraz z końcem wydarzenia.
                        </p>
                    )}
                    {truncationNotice && (
                        <p className="promo-modal-date-hint promo-modal-date-hint--warn">
                            {truncationNotice}
                        </p>
                    )}
                </div>

                <div className="promo-modal-actions">
                    <button type="button" className="promo-modal-cancel" onClick={onClose}>
                        Anuluj
                    </button>
                    <button type="submit" className="promo-modal-submit">
                        Promuj
                    </button>
                </div>
            </form>
        </Modal>
    );
}

export default PromoEventModal;
